// Tracker data on the client: one cached payload per program, changed optimistically
// with an undo toast. Every view (roadmap, board, list, panel) goes through here.
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from './api';
import { useToast } from '@/components/ui/toast';
import type { Role } from './types';

export type HealthLevel = 'GREEN' | 'AMBER' | 'RED';
export type TaskStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'BLOCKED' | 'IN_REVIEW' | 'DONE';
export type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface Task {
  id: string;
  phaseId: string;
  workstreamId: string | null;
  deliverableId: string | null;
  title: string;
  status: TaskStatus;
  priority: Priority;
  ownerId: string | null;
  startDate: string | null;
  dueDate: string | null;
  percent: number;
  rank: string;
  completedAt: string | null;
  checklistDone: number;
  checklistTotal: number;
  attachments: number;
  comments: number;
  raidOpen: boolean;
  blocks: string[];
  blockedBy: string[];
  meeting: { id: string; title: string; heldAt: string } | null;
}

export interface PhaseHealth {
  computed: HealthLevel;
  effective: HealthLevel;
  overridden: boolean;
  overrideReason: string | null;
  reasons: string[];
  overdueTasks: number;
}

export interface Milestone {
  id: string;
  name: string;
  plannedDate: string;
  forecastDate: string;
  achievedDate: string | null;
  status: 'PLANNED' | 'ACHIEVED' | 'CANCELLED';
}

export interface TrackerPhase {
  id: string;
  number: number;
  name: string;
  objective: string;
  plannedStart: string;
  plannedEnd: string;
  forecastStart: string;
  forecastEnd: string;
  windowNote: string | null;
  notes: string | null;
  health: PhaseHealth;
  taskProgress: { done: number; total: number };
  deliverableProgress: { done: number; total: number };
  milestones: Milestone[];
  workstreams: Array<{ id: string; name: string }>;
}

export interface TrackerDeliverable {
  id: string;
  name: string;
  phaseId: string;
  status: string;
  isProgramLevel: boolean;
  targetDate: string | null;
}

export interface TrackerData {
  today: string;
  health: HealthLevel;
  phasesInFlight: number;
  paProgress: { done: number; total: number };
  phases: TrackerPhase[];
  tasks: Task[];
  deliverables: TrackerDeliverable[];
  members: Array<{ userId: string; name: string; role: Role }>;
}

export const trackerKey = (programId: string) => ['tracker', programId] as const;

export function useTracker(programId: string) {
  return useQuery({
    queryKey: trackerKey(programId),
    queryFn: () => api.get<TrackerData>(`/api/programs/${programId}/tracker`),
    staleTime: 15_000,
  });
}

export const STATUS_ORDER: TaskStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'BLOCKED', 'IN_REVIEW', 'DONE'];
export const STATUS_LABEL: Record<TaskStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  IN_REVIEW: 'In review',
  DONE: 'Done',
};
export const PRIORITY_LABEL: Record<Priority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};
export const HEALTH_LABEL: Record<HealthLevel, string> = { GREEN: 'On track', AMBER: 'At risk', RED: 'Off track' };

export const isOverdue = (t: Pick<Task, 'status' | 'dueDate'>, today: string) =>
  t.status !== 'DONE' && Boolean(t.dueDate) && t.dueDate! < today;

/** The client mirror of the server's contributor rule, used only to shape the UI. */
export const CONTRIBUTOR_FIELDS = ['status', 'percent', 'checklist', 'description'] as const;

export type TaskPatch = Partial<
  Pick<
    Task,
    | 'title'
    | 'status'
    | 'priority'
    | 'ownerId'
    | 'startDate'
    | 'dueDate'
    | 'percent'
    | 'phaseId'
    | 'workstreamId'
    | 'deliverableId'
  >
> & { description?: string | null; checklist?: Array<{ id: string; text: string; done: boolean }> };

/**
 * Optimistic task changes with undo. The cache updates first; a failure rolls back
 * and shows why; success offers Undo, which sends the previous values back.
 */
export function useTaskActions(programId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = trackerKey(programId);
  const base = `/api/programs/${programId}`;

  const applyLocal = useCallback(
    (id: string, patch: Partial<Task>) =>
      qc.setQueryData<TrackerData>(key, (d) =>
        d ? { ...d, tasks: d.tasks.map((t) => (t.id === id ? { ...t, ...patch } : t)) } : d,
      ),
    [qc, key],
  );
  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['task', programId] });
  }, [qc, key, programId]);

  const patch = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TaskPatch; undoLabel?: string; silent?: boolean }) =>
      api.patch<Task>(`${base}/tasks/${id}`, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<TrackerData>(key)?.tasks.find((t) => t.id === id);
      applyLocal(id, patch as Partial<Task>);
      return { prev };
    },
    onError: (e, { id }, ctx) => {
      if (ctx?.prev) applyLocal(id, ctx.prev);
      toast.error(errorMessage(e));
    },
    onSuccess: (server, { id, patch: p, undoLabel, silent }, ctx) => {
      applyLocal(id, server);
      if (silent || !ctx?.prev) return;
      const before = Object.fromEntries(
        Object.keys(p).map((k) => [k, (ctx.prev as unknown as Record<string, unknown>)[k]]),
      );
      toast.ok(undoLabel ?? 'Task updated.', {
        label: 'Undo',
        onClick: () => patch.mutate({ id, patch: before as TaskPatch, silent: true }),
      });
    },
    onSettled: () => refresh(),
  });

  const move = useMutation({
    mutationFn: (v: {
      id: string;
      status?: TaskStatus;
      afterId?: string | null;
      beforeId?: string | null;
      rank?: string;
    }) => api.post<Task>(`${base}/tasks/${v.id}/move`, { status: v.status, afterId: v.afterId, beforeId: v.beforeId }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<TrackerData>(key)?.tasks.find((t) => t.id === v.id);
      applyLocal(v.id, { ...(v.status ? { status: v.status } : {}), ...(v.rank ? { rank: v.rank } : {}) });
      return { prev };
    },
    onError: (e, v, ctx) => {
      if (ctx?.prev) applyLocal(v.id, ctx.prev);
      toast.error(errorMessage(e));
    },
    onSuccess: (server, v, ctx) => {
      applyLocal(v.id, server);
      if (ctx?.prev && v.status && v.status !== ctx.prev.status) {
        toast.ok(`Moved to ${STATUS_LABEL[v.status]}.`, {
          label: 'Undo',
          onClick: () => patch.mutate({ id: v.id, patch: { status: ctx.prev!.status }, silent: true }),
        });
      }
    },
    onSettled: () => refresh(),
  });

  const create = useMutation({
    mutationFn: (body: TaskPatch & { title: string }) => api.post<Task>(`${base}/tasks`, body),
    onSuccess: (t) => {
      qc.setQueryData<TrackerData>(key, (d) => (d ? { ...d, tasks: [...d.tasks, t] } : d));
      toast.ok('Task added.');
    },
    onError: (e) => toast.error(errorMessage(e)),
    onSettled: () => refresh(),
  });

  const bulk = useMutation({
    mutationFn: (v: { ids: string[]; patch: TaskPatch }) => api.post<{ updated: number }>(`${base}/tasks/bulk`, v),
    onSuccess: (r) => toast.ok(`${r.updated} tasks updated.`),
    onError: (e) => toast.error(errorMessage(e)),
    onSettled: () => refresh(),
  });

  return { patch, move, create, bulk, refresh };
}

/** Lexicographic midpoint, mirroring the server, for instant optimistic ordering. */
export function rankBetween(a?: string | null, b?: string | null): string {
  const D = '0123456789abcdefghijklmnopqrstuvwxyz';
  const lo = a ?? '';
  const hi = b ?? '';
  if (lo && hi && lo >= hi) return lo + 'm';
  let out = '';
  for (let i = 0; ; i++) {
    const l = i < lo.length ? D.indexOf(lo[i]!) : 0;
    const h = hi && i < hi.length ? D.indexOf(hi[i]!) : D.length;
    if (h - l > 1) return out + D[Math.floor((l + h) / 2)];
    out += i < lo.length ? lo[i] : D[0];
    if (h - l === 1 && hi) return out + rankBetween(lo.slice(i + 1) || null, null);
  }
}
